import { computed, watch } from 'vue';
import { useStore } from '@dh-io-otb/shared/src/composables/store.composable';
import { i18n } from '@dh-io-otb/shared/src/services/translation.service';
import VehicleInformationService from '@dh-io-otb/shared/src/services/components/vehicle-information.service';
import {
  FiltersComposable,
  FilterOption,
  FilterDealerOption,
  FiltersDefaultValue,
  FilterType,
  AllVehicleFilters,
  VehicleFilters,
  FilterGroupType,
  MotorisationProperty,
  MotorisationLifecyle,
} from '../models/entities/filters.model';
import {
  TRACKING_EVENT_ACTION,
  TRACKING_EVENT_CATEGORY,
  TRACKING_EVENT_LABEL,
  TRACKING_EVENT_NAME,
} from '@dh-io-otb/shared/src/models/constants';
import { EventTrackingInfo } from '@dh-io-otb/shared/src/models/entities/tracking.model';
import TrackingService from '@dh-io-otb/shared/src/services/tracking.service';
import { CONTEXT_OPTIONS } from '../models/constants';
import { useSummary } from './summary.composable';
import UtilsService from '@dh-io-otb/shared/src/services/utils.service';

const useFilters = (): FiltersComposable => {
  const { state, commit } = useStore();
  const { summaryVehicleInformation } = useSummary();
  const convertArrayToInteractiveTagOption = (
    options: Array<string> = [],
    prefix?: string,
    suffix?: string,
    directTagName?: string,
  ): FilterOption[] => {
    return options.map((option): FilterOption => {
      return {
        text: directTagName
          ? directTagName.toString()
          : i18n.t(`${prefix ? `${prefix}.` : ''}${option?.toLowerCase()}${suffix ? `.${suffix}` : ''}`),
        value: option,
        disabled: false,
      };
    });
  };

  const findArrayName = (str: string, obj: AllVehicleFilters): string | boolean => {
    for (const key in obj) {
      if (Array.isArray(obj[key])) {
        for (let i = 0; i < obj[key].length; i++) {
          if (obj[key][i].value === str) {
            return key;
          }
        }
      }
    }
    return false;
  };

  const filteredMotorisations = computed(() => {
    const archivedMotorisations: any[] = [];
    const notArchivedMotorisations: any[] = [];
    const notArchivedMotorisationNames: any[] = [];
    const allMotorisations = state.otbcFlow.selectedModelMotorisations;

    allMotorisations.forEach((motorisation) => {
      if (motorisation.lifeCycle === MotorisationLifecyle.ARCHIVED) {
        archivedMotorisations.push(motorisation);
      } else {
        notArchivedMotorisations.push(motorisation);
        const formattedName = VehicleInformationService.getMotorisationLabel(motorisation.name, motorisation.engine);
        notArchivedMotorisationNames.push(formattedName);
      }
    });
    if (!archivedMotorisations.length || !state.otbcFlow.bookingsSearchResults.length) return notArchivedMotorisations;

    const archivedMotorisationsWithUniqueName = archivedMotorisations.filter(
      (motorisation) =>
        !notArchivedMotorisationNames.includes(
          VehicleInformationService.getMotorisationLabel(motorisation.name, motorisation.engine),
        ),
    );

    if (!archivedMotorisationsWithUniqueName.length) return notArchivedMotorisations;

    const allMotorisationsResultsIds = state.otbcFlow.bookingsSearchResults.map((vehicle) => vehicle.motorisationId);

    const archivedMotorisationWithResults = archivedMotorisationsWithUniqueName.filter((motorisation) =>
      allMotorisationsResultsIds.includes(motorisation.id),
    );

    if (!archivedMotorisationWithResults.length) return notArchivedMotorisations;

    const archivedMotorisationWithResultsIds = archivedMotorisationWithResults.map((motorisation) => motorisation.id);

    //To mantain the order of the motorisations
    const filteredMotorisations = allMotorisations.filter((motorisation) => {
      if (motorisation.lifeCycle !== MotorisationLifecyle.ARCHIVED) return motorisation;
      if (archivedMotorisationWithResultsIds.includes(motorisation.id)) return motorisation;
    });
    return filteredMotorisations;
  });

  // FILTER OPTIONS BUILDER COMPUTEDS

  const fuelTypeFilters = computed((): FilterOption[] => {
    let initiallyAvailableFuelTypes: any;

    if (filteredMotorisations.value) {
      initiallyAvailableFuelTypes = Array.from(
        new Set(filteredMotorisations.value.map((item) => item.fuelType).flat()),
      );
    }
    if (initiallyAvailableFuelTypes && initiallyAvailableFuelTypes.length === 1) {
      return convertArrayToInteractiveTagOption(initiallyAvailableFuelTypes, 'shared.fuel');
    }

    return [
      {
        text: i18n.t('model.filter.option.all'),
        value: FiltersDefaultValue.ALLFUELTYPE,
        disabled: false,
      },
      ...convertArrayToInteractiveTagOption(initiallyAvailableFuelTypes, 'shared.fuel'),
    ];
  });

  const transmissionFilters = computed((): FilterOption[] => {
    let initiallyAvailableTransmissions: any;

    if (filteredMotorisations.value) {
      initiallyAvailableTransmissions = Array.from(
        new Set(filteredMotorisations.value.map((item) => item.transmissions).flat()),
      );
    }
    if (initiallyAvailableTransmissions && initiallyAvailableTransmissions.length === 1) {
      return convertArrayToInteractiveTagOption(initiallyAvailableTransmissions, 'shared.transmission');
    }
    return [
      {
        text: i18n.t('model.filter.option.all'),
        value: FiltersDefaultValue.ALLTRANSMISSION,
        disabled: false,
      },
      ...convertArrayToInteractiveTagOption(initiallyAvailableTransmissions, 'shared.transmission'),
    ];
  });

  const motorisationFilters = computed((): FilterOption[] => {
    const motorisationFilterOptions: FilterOption[] = [];
    if (state.otbcFlow.availableMotorisationOptions?.length) {
      state.otbcFlow.availableMotorisationOptions.map((motorisation) => {
        motorisationFilterOptions.push({
          text: VehicleInformationService.getMotorisationLabel(motorisation.name, motorisation.engine),
          value: motorisation.id,
          disabled: false,
        });
      });
    }
    return [
      {
        text: i18n.t('model.filter.option.all'),
        value: FiltersDefaultValue.ALLMOTORISATION,
        disabled: false,
      },
      ...motorisationFilterOptions,
    ];
  });

  const timePeriodFilters = computed((): FilterOption[] => {
    const timePeriodObjects = state.otbcFlow.bookingsSearchResults.map((item) => item.timePeriodAvailability);
    const initiallyAvailableTimePeriods: any = [
      ...new Set(
        timePeriodObjects.reduce((accumulator, obj) => {
          return [...accumulator, ...Object.keys(obj).filter((key) => obj[key] === true)];
        }, []),
      ),
    ];
    return [
      {
        text: 'All Day',
        value: FiltersDefaultValue.ALLDAY,
        disabled: false,
      },
      ...convertArrayToInteractiveTagOption(initiallyAvailableTimePeriods, 'schedule.period'),
    ];
  });

  const dealersFilters = computed((): FilterDealerOption[] => {
    const dealers = state.otbcFlow.dealersSearchResults.map((dealer): FilterDealerOption => {
      return {
        text: dealer.computedName,
        address: dealer.address.computedAddress,
        distanceValue: dealer.address.distance,
        distanceUnit: dealer.address.unit,
        value: dealer.id,
        disabled: false,
      };
    });
    // TODO In the future we will need to order the dealers by distance
    const orderedDealers = UtilsService.sortByAlphabeticalOrder(dealers, 'text');
    return orderedDealers;
  });

  watch(
    () => dealersFilters.value,
    (dealersList) => {
      commit('availableDealersOptions', dealersList);
    },
  );
  const driveTypeFilters = computed((): FilterOption[] => {
    let initiallyAvailableDriveTypes: any;

    if (filteredMotorisations.value) {
      initiallyAvailableDriveTypes = Array.from(
        new Set(filteredMotorisations.value.map((item) => item.driveType).flat()),
      );
    }
    if (initiallyAvailableDriveTypes && initiallyAvailableDriveTypes.length === 1) {
      return convertArrayToInteractiveTagOption(
        initiallyAvailableDriveTypes,
        'shared.driveType',
        '',
        initiallyAvailableDriveTypes,
      );
    }

    return [
      {
        text: i18n.t('model.filter.option.all'),
        value: FiltersDefaultValue.ALLDRIVETYPE,
        disabled: false,
      },
      ...convertArrayToInteractiveTagOption(initiallyAvailableDriveTypes, 'shared.driveType'),
    ];
  });

  const wheelBaseFilters = computed((): FilterOption[] => {
    const wheelBases: Array<{ id: string; name: string }> = [];

    if (filteredMotorisations.value) {
      const wheelBaseSet = new Set();
      for (const item of filteredMotorisations.value) {
        if (item.wheelBase) {
          const { id, name } = item.wheelBase;
          const key = `${id}:${name}`;
          if (!wheelBaseSet.has(key)) {
            wheelBases.push({ id, name });
            wheelBaseSet.add(key);
          }
        }
      }
    }

    if (wheelBases.length === 1) {
      return [
        {
          text: wheelBases[0].name,
          value: wheelBases[0].id,
          disabled: false,
        },
      ];
    }

    return [
      {
        text: i18n.t('model.filter.option.all'),
        value: FiltersDefaultValue.ALLWHEELBASE,
        disabled: false,
      },
      ...wheelBases.map(({ id, name }) => ({
        text: name,
        value: id,
        disabled: false,
      })),
    ];
  });
  const weightFilters = computed((): FilterOption[] => {
    let initiallyAvailableWeightTypes: any;

    if (filteredMotorisations.value) {
      initiallyAvailableWeightTypes = Array.from(
        new Set(filteredMotorisations.value.map((item) => item.weightCategory).flat()),
      );
    }
    if (initiallyAvailableWeightTypes && initiallyAvailableWeightTypes.length === 1) {
      return convertArrayToInteractiveTagOption(initiallyAvailableWeightTypes, 'shared.weightCategory');
    }
    return [
      {
        text: i18n.t('model.filter.option.all'),
        value: FiltersDefaultValue.ALLWEIGHT,
        disabled: false,
      },
      ...convertArrayToInteractiveTagOption(initiallyAvailableWeightTypes, 'shared.weightCategory'),
    ];
  });

  const allVehicleFilters = computed((): AllVehicleFilters => {
    return {
      fuelTypeFilters: fuelTypeFilters.value,
      transmissionFilters: transmissionFilters.value,
      motorisationFilters: motorisationFilters.value,
      dealersFilters: dealersFilters.value,
      timePeriodFilters: timePeriodFilters.value,
      driveTypeFilters: driveTypeFilters.value,
      wheelBaseFilters: wheelBaseFilters.value,
      weightFilters: weightFilters.value,
    };
  });

  const initializeFilters = (): void => {
    commit('allVehicleFilters', allVehicleFilters.value);
    checkAvailableOptions();
    commit('availableDealersOptions', dealersFilters.value);
    commit('availableTimePeriodOptions', timePeriodFilters.value);
  };

  const updateSelectedFilters = (): void => {
    commit('filtersApplied', {
      fuelType: state.otbcFlow.selectedFuelType || state.otbcFlow.confirmedFuelType,
      transmission: state.otbcFlow.selectedTransmission || state.otbcFlow.confirmedTransmission,
      motorisation: state.otbcFlow.selectedMotorisation || state.otbcFlow.confirmedMotorisation,
      dealers: state.otbcFlow.selectedDealerIds,
      timePeriod: state.otbcFlow.selectedTimePeriod,
      driveType: state.otbcFlow.selectedDriveType || state.otbcFlow.confirmedDriveType,
      wheelBase: state.otbcFlow.selectedWheelBase || state.otbcFlow.confirmedWheelBase,
      weight: state.otbcFlow.selectedWeight || state.otbcFlow.confirmedWeight,
    });
    filteredBookingResults();
    commit('isFilterSelected', true);
    // Check if any filter excluding timePeriod and dealers is truthy
    const isVehicleFilterSelected = !!(
      state.otbcFlow.selectedFuelType ||
      state.otbcFlow.selectedTransmission ||
      state.otbcFlow.selectedMotorisation ||
      state.otbcFlow.selectedDriveType ||
      state.otbcFlow.selectedWheelBase ||
      state.otbcFlow.selectedWeight
    );
    const isVehicleFilterConfirmed = !!(
      state.otbcFlow.confirmedFuelType ||
      state.otbcFlow.confirmedTransmission ||
      state.otbcFlow.confirmedMotorisation ||
      state.otbcFlow.confirmedDriveType ||
      state.otbcFlow.confirmedWheelBase ||
      state.otbcFlow.confirmedWeight
    );
    commit('isVehicleFilterSelected', isVehicleFilterSelected);
    commit('isVehicleFilterConfirmed', isVehicleFilterConfirmed);

    if (summaryVehicleInformation.value) {
      commit('vehiclePreferences', VehicleInformationService.getVehicleInformation(summaryVehicleInformation.value));
    }
  };

  const checkAvailableOptions = (): void => {
    filterAvailableFuelTypeOptions();
    filterAvailableTransmissionOptions();
    if (state.configuration.context === CONTEXT_OPTIONS.VANS) {
      filterAvailableDriveTypeOptions();
      filterAvailableWheelBaseOptions();
      filterAvailableWeightOptions();
    }
    filterAvailableMotorisationOptions();
  };

  const filterAvailableFuelTypeOptions = (): void => {
    const allFuelTypeOptions = state.otbcFlow.allVehicleFilters.fuelTypeFilters;
    const availableMotorisations = getAvailableMotorisations(FilterType.FUELTYPE);
    let availableFuelTypes = getMotorisationPropertyAvailable(availableMotorisations, MotorisationProperty.FUEL_TYPE);
    if (state.otbcFlow.selectedMotorisation) {
      availableFuelTypes = [state.otbcFlow.selectedMotorisation.fuelType];
    }
    const updatedFuelTypes = allFuelTypeOptions.map((option) => ({
      ...option,
      disabled: option.value !== FiltersDefaultValue.ALLFUELTYPE && !availableFuelTypes.includes(option.value),
    }));
    commit('availableFuelOptions', updatedFuelTypes);
  };

  const filterAvailableTransmissionOptions = (): void => {
    const allTransmissionOptions = state.otbcFlow.allVehicleFilters.transmissionFilters;
    const availableMotorisations = getAvailableMotorisations(FilterType.TRANSMISSION);
    let availableTransmissions = getTransmissionsAvailable(availableMotorisations);
    if (state.otbcFlow.selectedMotorisation) {
      availableTransmissions = state.otbcFlow.selectedMotorisation.transmissions;
    }
    const updatedTransmissions = allTransmissionOptions.map((option) => ({
      ...option,
      disabled: option.value !== FiltersDefaultValue.ALLTRANSMISSION && !availableTransmissions.includes(option.value),
    }));
    commit('availableTransmissionOptions', updatedTransmissions);
  };

  const filterAvailableMotorisationOptions = (): void => {
    const availableMotorisations = getAvailableMotorisations(FilterType.MOTORISATION);
    commit('availableMotorisationOptions', availableMotorisations);
  };

  const filterAvailableDriveTypeOptions = (): void => {
    const allDriveTypeOptions = state.otbcFlow.allVehicleFilters.driveTypeFilters;
    const availableMotorisations = getAvailableMotorisations(FilterType.DRIVE_TYPE);
    let availableDriveTypes = getMotorisationPropertyAvailable(availableMotorisations, MotorisationProperty.DRIVE_TYPE);
    if (state.otbcFlow.selectedMotorisation) {
      availableDriveTypes = [state.otbcFlow.selectedMotorisation.driveType];
    }
    const updatedDriveTypeOptions = allDriveTypeOptions.map((option) => ({
      ...option,
      disabled: option.value !== FiltersDefaultValue.ALLDRIVETYPE && !availableDriveTypes.includes(option.value),
    }));
    commit('availableDriveTypeOptions', updatedDriveTypeOptions);
  };

  const filterAvailableWeightOptions = (): void => {
    const allWeightOptions = state.otbcFlow.allVehicleFilters.weightFilters;
    const availableMotorisations = getAvailableMotorisations(FilterType.WEIGHT);
    let availableWeights = getMotorisationPropertyAvailable(
      availableMotorisations,
      MotorisationProperty.WEIGHT_CATEGORY,
    );
    if (state.otbcFlow.selectedMotorisation) {
      availableWeights = [state.otbcFlow.selectedMotorisation.weightCategory];
    }
    const updatedWeightOptions = allWeightOptions.map((option) => ({
      ...option,
      disabled: option.value !== FiltersDefaultValue.ALLWEIGHT && !availableWeights.includes(option.value),
    }));
    commit('availableWeightOptions', updatedWeightOptions);
  };

  const filterAvailableWheelBaseOptions = (): void => {
    const allWheelBaseOptions = state.otbcFlow.allVehicleFilters.wheelBaseFilters;
    const availableMotorisations = getAvailableMotorisations(FilterType.WHEELBASE);
    let availableWheelBase = getWheelBasesAvailable(availableMotorisations);
    if (state.otbcFlow.selectedMotorisation) {
      availableWheelBase = [state.otbcFlow.selectedMotorisation.wheelBase?.id];
    }
    const updatedWheelBaseOptions = allWheelBaseOptions.map((option) => ({
      ...option,
      disabled: option.value !== FiltersDefaultValue.ALLWHEELBASE && !availableWheelBase.includes(option.value),
    }));
    commit('availableWheelBaseOptions', updatedWheelBaseOptions);
  };

  const filteredBookingResults = (): void => {
    const allVehicles = state.otbcFlow.bookingsSearchResults;
    const filter = {
      fuelType: state.otbcFlow.selectedFuelType,
      transmission: state.otbcFlow.selectedTransmission,
      timePeriod: state.otbcFlow.selectedTimePeriod,
      motorisation: state.otbcFlow.selectedMotorisation,
      dealers: state.otbcFlow.selectedDealerIds,
      driveType: state.otbcFlow.selectedDriveType,
      wheelBase: state.otbcFlow.selectedWheelBase,
      weight: state.otbcFlow.selectedWeight,
    };

    let filteredVehicles = allVehicles.filter(
      (vehicle) =>
        (!filter.fuelType || vehicle.fuelType === filter.fuelType) &&
        (!filter.transmission || vehicle.transmission.includes(filter.transmission)) &&
        (!filter.timePeriod || vehicle.timePeriodAvailability[filter.timePeriod]) &&
        (!filter.dealers || filter.dealers.length === 0 || filter.dealers.includes(vehicle.dealer.id)),
    );

    if (filter.motorisation) {
      const filterMotorisationName = VehicleInformationService.getMotorisationLabel(
        filter.motorisation.name,
        filter.motorisation.engine,
      );
      filteredVehicles = filteredVehicles.filter((vehicle) => {
        const vehicleMotorisationName = VehicleInformationService.getMotorisationLabel(vehicle.name, vehicle.engine);
        return vehicle.motorisationId === filter.motorisation.id || filterMotorisationName === vehicleMotorisationName;
      });
    }

    if (state.configuration.context === CONTEXT_OPTIONS.VANS) {
      filteredVehicles = filteredVehicles.filter(
        (vehicle) =>
          (!filter.driveType || vehicle.driveType === filter.driveType) &&
          (!filter.wheelBase || vehicle.wheelBase?.id === filter.wheelBase) &&
          (!filter.weight || vehicle.weightCategory === filter.weight),
      );
    }
    commit('filteredBookingsSearchResults', filteredVehicles);
  };

  const getAvailableMotorisations = (filterTypeToExclude: FilterType): any[] => {
    const filter = {
      fuelType: state.otbcFlow.selectedFuelType,
      transmissions: state.otbcFlow.selectedTransmission,
      motorisation: state.otbcFlow.selectedMotorisation,
      driveType: state.otbcFlow.selectedDriveType,
      wheelBase: state.otbcFlow.selectedWheelBase,
      weight: state.otbcFlow.selectedWeight,
    };
    delete filter[filterTypeToExclude];
    const filteredOptions = filteredMotorisations.value.filter(
      (motorisation) =>
        (!filter.fuelType || motorisation.fuelType === filter.fuelType) &&
        (!filter.transmissions || motorisation.transmissions.includes(filter.transmissions)) &&
        (!filter.driveType || motorisation.driveType === filter.driveType) &&
        (!filter.motorisation || motorisation.motorisationId === filter.motorisation.id) &&
        (!filter.wheelBase || motorisation.wheelBase?.id === filter.wheelBase) &&
        (!filter.weight || motorisation.weightCategory === filter.weight),
    );
    return filteredOptions;
  };

  watch(() => state.otbcFlow.bookingsSearchResults, filteredBookingResults);

  const resetFilters = (typeOfFilters: string): void => {
    if (typeOfFilters === 'vehicle') {
      commit('selectedMotorisation', '');
      commit('selectedFuelType', '');
      commit('selectedTransmission', '');
      commit('selectedDriveType', '');
      commit('selectedWheelBase', '');
      commit('selectedWeight', '');
      commit('availableFuelOptions', fuelTypeFilters.value);
      commit('availableTransmissionOptions', transmissionFilters.value);
      commit('availableMotorisationOptions', getAvailableMotorisations(FilterType.MOTORISATION));
      commit('availableDriveTypeOptions', driveTypeFilters.value);
      commit('availableWheelBaseOptions', wheelBaseFilters.value);
      commit('availableWeightOptions', weightFilters.value);
      commit('filtersApplied', {
        fuelType: FiltersDefaultValue.ALLFUELTYPE,
        transmission: FiltersDefaultValue.ALLTRANSMISSION,
        motorisation: '',
        timePeriod: state.otbcFlow.selectedTimePeriod,
        dealers: state.otbcFlow.selectedDealerIds,
        driveType: FiltersDefaultValue.ALLDRIVETYPE,
        wheelBase: FiltersDefaultValue.ALLWHEELBASE,
        weight: FiltersDefaultValue.ALLWEIGHT,
      });
    }
    if (typeOfFilters === 'timePeriod') {
      commit('availableTimePeriodOptions', timePeriodFilters.value);
      commit('selectedTimePeriod', '');
      commit('filtersApplied', {
        fuelType: state.otbcFlow.selectedFuelType,
        transmission: state.otbcFlow.selectedTransmission,
        motorisation: state.otbcFlow.selectedMotorisation,
        dealers: state.otbcFlow.selectedDealerIds,
        driveType: state.otbcFlow.selectedDriveType,
        wheelBase: state.otbcFlow.selectedWheelBase,
        weight: state.otbcFlow.selectedWeight,
      });
    }
    if (typeOfFilters === 'dealers') {
      commit('selectedDealerIds', []);
      commit('availableDealersOptions', dealersFilters.value);
      commit('dealerTextFilterSearch', '');
      commit('filtersApplied', {
        fuelType: state.otbcFlow.selectedFuelType,
        transmission: state.otbcFlow.selectedTransmission,
        motorisation: state.otbcFlow.selectedMotorisation,
        dealers: '',
        timePeriod: state.otbcFlow.selectedTimePeriod,
        driveType: state.otbcFlow.selectedDriveType,
        wheelBase: state.otbcFlow.selectedWheelBase,
        weight: state.otbcFlow.selectedWeight,
      });
    }
    commit('filteredBookingsSearchResults', state.otbcFlow.bookingsSearchResults);
  };

  const resetFiltersIfNotConfirmed = (): void => {
    if (state.otbcFlow.selectedFuelType !== state.otbcFlow.confirmedFuelType) {
      commit('selectedFuelType', '');
      commit('filtersApplied', {
        fuelType: '',
        transmission: state.otbcFlow.selectedTransmission,
        motorisation: state.otbcFlow.selectedMotorisation,
      });
    }
    if (state.otbcFlow.selectedTransmission !== state.otbcFlow.confirmedTransmission) {
      commit('selectedTransmission', '');
      commit('filtersApplied', {
        fuelType: state.otbcFlow.selectedFuelType,
        transmission: '',
        motorisation: state.otbcFlow.selectedMotorisation,
      });
    }
    if (state.otbcFlow.selectedMotorisation !== state.otbcFlow.confirmedMotorisation) {
      commit('selectedMotorisation', '');
      commit('filtersApplied', {
        fuelType: state.otbcFlow.selectedFuelType,
        transmission: state.otbcFlow.selectedTransmission,
        motorisation: '',
      });
    }
  };

  const resetAllFiltersAndAvailableMotorisations = () => {
    resetFilters(FilterGroupType.VEHICLE);
    resetFilters(FilterGroupType.DEALERS);
    resetFilters(FilterGroupType.TIME_PERIOD);
    trackClearFilterEvents(TRACKING_EVENT_ACTION.FILTER_RESULTS);
    updateSelectedFilters();
    commit('isFilterSelected', false);
    commit('isVehicleFilterSelected', false);
  };

  const resetAllVehicleFiltersInRequest = () => {
    resetFilters(FilterGroupType.VEHICLE);
    trackClearFilterEvents(TRACKING_EVENT_ACTION.FILTER_RESULTS);
    commit('isFilterSelected', false);
    commit('isVehicleFilterSelected', false);
  };

  const resetFilterByProperty = (filterName: FilterType): void => {
    const filtersApplied = state.otbcFlow.filtersApplied;
    switch (filterName) {
      case FilterType.FUELTYPE:
        commit('availableFuelOptions', fuelTypeFilters.value);
        commit('selectedFuelType', '');
        filtersApplied.fuelType = FiltersDefaultValue.ALLFUELTYPE;
        break;
      case FilterType.TRANSMISSION:
        commit('availableTransmissionOptions', transmissionFilters.value);
        commit('selectedTransmission', '');
        filtersApplied.transmission = FiltersDefaultValue.ALLTRANSMISSION;
        break;
      case FilterType.MOTORISATION:
        commit('availableMotorisationOptions', motorisationFilters.value);
        commit('selectedMotorisation', '');
        filtersApplied.motorisation = '';
        break;
      case FilterType.DRIVE_TYPE:
        commit('availableDriveTypeOptions', driveTypeFilters.value);
        commit('selectedDriveType', '');
        filtersApplied.driveType = FiltersDefaultValue.ALLDRIVETYPE;
        break;
      case FilterType.WHEELBASE:
        commit('availableWheelBaseOptions', wheelBaseFilters.value);
        commit('selectedWheelBase', '');
        filtersApplied.wheelBase = FiltersDefaultValue.ALLWHEELBASE;
        break;
      case FilterType.WEIGHT:
        commit('availableWeightOptions', weightFilters.value);
        commit('selectedWeight', '');
        filtersApplied.weight = FiltersDefaultValue.ALLWEIGHT;
        break;
      case FilterType.TIME_PERIOD:
        commit('availableTimePeriodOptions', timePeriodFilters.value);
        commit('selectedTimePeriod', '');
        filtersApplied.timePeriod = '';
        break;
    }
    commit('filtersApplied', filtersApplied);
  };
  const setSingleOptionFiltersAsSelected = () => {
    const filterTypes = [
      {
        filterOptions: fuelTypeFilters,
        commitType: 'selectedFuelType',
        defaultValue: FiltersDefaultValue.ALLFUELTYPE,
        vansSpecific: false,
      },
      {
        filterOptions: transmissionFilters,
        commitType: 'selectedTransmission',
        defaultValue: FiltersDefaultValue.ALLTRANSMISSION,
        vansSpecific: false,
      },
      {
        filterOptions: motorisationFilters,
        commitType: 'selectedMotorisation',
        defaultValue: FiltersDefaultValue.ALLMOTORISATION,
        vansSpecific: false,
      },
      {
        filterOptions: driveTypeFilters,
        commitType: 'selectedDriveType',
        defaultValue: FiltersDefaultValue.ALLDRIVETYPE,
        vansSpecific: true,
      },
      {
        filterOptions: wheelBaseFilters,
        commitType: 'selectedWheelBase',
        defaultValue: FiltersDefaultValue.ALLWHEELBASE,
        vansSpecific: true,
      },
      {
        filterOptions: weightFilters,
        commitType: 'selectedWeight',
        defaultValue: FiltersDefaultValue.ALLWEIGHT,
        vansSpecific: true,
      },
    ];

    filterTypes.forEach(({ filterOptions, commitType, defaultValue, vansSpecific }) => {
      if (vansSpecific && !state.otbcFlow.isVansContext) {
        return;
      }

      const options = filterOptions.value;
      if (options.length === 1 && options[0].value !== defaultValue) {
        commit(commitType, options[0].value);
        updateSelectedFilters();
      }
    });
  };

  const selectOption = ({ value }) => {
    const type = findArrayName(value, state.otbcFlow.allVehicleFilters);
    if (type === VehicleFilters.FUELTYPE_FILTERS) {
      handleFuelTypeSelection(value);
      trackFilterEvents(TRACKING_EVENT_ACTION.VEHICLE, TRACKING_EVENT_LABEL.FUEL);
    } else if (type === VehicleFilters.TRANSMISSION_FILTERS) {
      handleTransmissionSelection(value);
      trackFilterEvents(TRACKING_EVENT_ACTION.VEHICLE, TRACKING_EVENT_LABEL.TRANSMISSION);
    } else if (type === VehicleFilters.TIME_PERIOD_FILTERS) {
      handleTimePeriodSelection(value);
      trackFilterEvents(TRACKING_EVENT_ACTION.PERIOD);
    } else if (type === VehicleFilters.DRIVE_TYPE_FILTERS) {
      handleDriveTypeSelection(value);
    } else if (type === VehicleFilters.WHEELBASE_FILTERS) {
      handleWheelBaseSelection(value);
    } else if (type === VehicleFilters.WEIGHT_FILTERS) {
      handleWeightSelection(value);
    }
    updateSelectedFilters();
    checkAvailableOptions();
    filteredBookingResults();
  };

  const handleFuelTypeSelection = (value: FiltersDefaultValue) => {
    if (value === FiltersDefaultValue.ALLFUELTYPE) {
      resetFilterByProperty(FilterType.FUELTYPE);
    } else {
      commit('selectedFuelType', value);
    }
  };

  const handleTransmissionSelection = (value: FiltersDefaultValue) => {
    if (value === FiltersDefaultValue.ALLTRANSMISSION) {
      resetFilterByProperty(FilterType.TRANSMISSION);
    } else {
      commit('selectedTransmission', value);
    }
  };

  const handleTimePeriodSelection = (value: FiltersDefaultValue) => {
    if (value === FiltersDefaultValue.ALLDAY) {
      resetFilterByProperty(FilterType.TIME_PERIOD);
    } else {
      commit('selectedTimePeriod', value);
    }
  };

  const handleDriveTypeSelection = (value) => {
    if (value === FiltersDefaultValue.ALLDRIVETYPE) {
      resetFilterByProperty(FilterType.DRIVE_TYPE);
    } else {
      commit('selectedDriveType', value);
    }
  };

  const handleWheelBaseSelection = (value) => {
    if (value === FiltersDefaultValue.ALLWHEELBASE) {
      resetFilterByProperty(FilterType.WHEELBASE);
    } else {
      commit('selectedWheelBase', value);
    }
  };

  const handleWeightSelection = (value) => {
    if (value === FiltersDefaultValue.ALLWEIGHT) {
      resetFilterByProperty(FilterType.WEIGHT);
    } else {
      commit('selectedWeight', value);
    }
  };

  const motorisationChange = (event: any): void => {
    const value = event.target.value;
    if (value === FiltersDefaultValue.ALLMOTORISATION) {
      resetFilterByProperty(FilterType.MOTORISATION);
    } else {
      const motorisation = filteredMotorisations.value.find((motorisation: { id: any }) => motorisation.id === value);
      commit('selectedMotorisation', motorisation);
      updateSelectedFilters();
    }
    checkAvailableOptions();
    filteredBookingResults();
    trackFilterEvents(TRACKING_EVENT_ACTION.VEHICLE, TRACKING_EVENT_LABEL.ENGINE);
  };

  const dealerChange = (event: any): void => {
    const selectedDealerIds = state.otbcFlow.selectedDealerIds;
    const newDealerId = event.target.value;
    const index = selectedDealerIds.indexOf(newDealerId);
    if (index !== -1) {
      selectedDealerIds.splice(index, 1);
    } else {
      selectedDealerIds.push(newDealerId);
    }
    commit('selectedDealerIds', selectedDealerIds);
    trackFilterEvents(TRACKING_EVENT_ACTION.RETAILER);
    updateSelectedFilters();
  };

  const getMotorisationPropertyAvailable = (motorisations: any[], property: string) => {
    return [...new Set(motorisations.flatMap((motorisation: any) => motorisation[property]))];
  };

  const getTransmissionsAvailable = (motorisations: any) => {
    const allTransmissions = <any>[];
    motorisations.forEach((motorisation: any) => {
      motorisation.transmissions.map((transmission) => allTransmissions.push(transmission));
    });
    return [...new Set(allTransmissions)];
  };

  const getWheelBasesAvailable = (motorisations: any) => {
    const allWheelBases = motorisations.map((motorisation) => motorisation.wheelBase.id);
    return [...new Set(allWheelBases)];
  };

  const trackFilterEvents = (action: TRACKING_EVENT_ACTION, label?: TRACKING_EVENT_LABEL) => {
    if (TrackingService.hasEventBeenCalled(action, label)) return;
    const event: EventTrackingInfo = {
      category: TRACKING_EVENT_CATEGORY.FILTER,
      name: TRACKING_EVENT_NAME.APPLY_FILTER,
      action,
      label,
    };
    TrackingService.trackEvent(event);
  };

  const trackClearFilterEvents = (action: TRACKING_EVENT_ACTION, name?: TRACKING_EVENT_NAME) => {
    const label = TRACKING_EVENT_LABEL.CLEAR_ALL;
    if (TrackingService.hasEventBeenCalled(action, label)) return;
    const event: EventTrackingInfo = {
      category: TRACKING_EVENT_CATEGORY.FILTER,
      name,
      label,
      action,
    };
    TrackingService.trackEvent(event);
  };

  const numberOfFilteredBookingResults = computed((): number => {
    filteredBookingResults();
    return state.otbcFlow.filteredBookingsSearchResults.length;
  });

  const enableClearVehiclePreferencesBtn = computed(() => {
    const {
      selectedFuelType,
      selectedTransmission,
      selectedMotorisation,
      selectedDriveType,
      selectedWheelBase,
      selectedWeight,
    } = state.otbcFlow;
    return !(
      selectedFuelType ||
      selectedTransmission ||
      selectedMotorisation ||
      selectedDriveType ||
      selectedWheelBase ||
      selectedWeight
    );
  });

  const enableClearTimePeriodPreferencesBtn = computed((): boolean => {
    return !state.otbcFlow.selectedTimePeriod;
  });

  const enableClearDealersPreferencesBtn = computed(() => {
    const hasTextFilterSearch = !!state.otbcFlow.dealerTextFilterSearch;
    const hasSelectedDealers = !!state.otbcFlow.selectedDealerIds.length;
    return hasTextFilterSearch || hasSelectedDealers;
  });

  return {
    // HELPER FUNCTIONS
    convertArrayToInteractiveTagOption,
    findArrayName,
    allVehicleFilters,
    updateSelectedFilters,
    resetAllFiltersAndAvailableMotorisations,
    getAvailableMotorisations,
    initializeFilters,
    setSingleOptionFiltersAsSelected,

    // FILTER OPTIONS BUILDER COMPUTEDS
    fuelTypeFilters,
    transmissionFilters,
    motorisationFilters,
    timePeriodFilters,
    dealersFilters,

    filteredMotorisations,

    // VANS SPECIFIC FILTER OPTIONS BUILDER COMPUTEDS
    driveTypeFilters,
    wheelBaseFilters,
    weightFilters,

    // FILTERING
    filterAvailableFuelTypeOptions,
    filterAvailableTransmissionOptions,
    filterAvailableMotorisationOptions,
    filteredBookingResults,
    filterAvailableDriveTypeOptions,
    filterAvailableWheelBaseOptions,
    filterAvailableWeightOptions,
    checkAvailableOptions,
    numberOfFilteredBookingResults,

    // OPTION SELECTION
    selectOption,
    motorisationChange,
    dealerChange,

    // FILTER OPTION RESET FUNCTIONS
    resetFilters,
    resetFilterByProperty,
    enableClearVehiclePreferencesBtn,
    enableClearTimePeriodPreferencesBtn,
    enableClearDealersPreferencesBtn,
    handleDriveTypeSelection,
    handleWheelBaseSelection,
    handleWeightSelection,
    resetFiltersIfNotConfirmed,
    resetAllVehicleFiltersInRequest,

    //TRACKING
    trackFilterEvents,
    trackClearFilterEvents,
  };
};

export { useFilters };
